import React from 'react'
import PropTypes from 'prop-types'
import { Field } from 'react-formality'
import { TextField, IconButton, FormControl, Button, Select, MenuItem, InputLabel } from '@material-ui/core'
import ArrowUpward from '@material-ui/icons/ArrowUpward'
import ArrowDownward from '@material-ui/icons/ArrowDownward'
import Delete from '@material-ui/icons/Delete'
import generateRandomID from 'uuid/v4'
import { firestore } from './utilities/firebase'
import Form from './components/Form'
import ColourEditor from './components/ColourEditor'
import errorTypes from './enums/errorTypes'
import messageTypes from './enums/messageTypes'
import itemTypes from './enums/itemTypes'
import AutoComplete from './components/AutoComplete'
import getNormalisedParameters from './utilities/getNormalisedParameters'
import arrayMove from './utilities/arrayMove'
import connectPropTypes from './utilities/connectPropTypes'
import styles from './ItemEdit.icss'
import getDecodedURIComponent from './utilities/getDecodedURIComponent'
import Item from './utilities/Data/Item'

//	Segment colours, extract just the full image
const segmentImages = (images) => {
	const output = []
	let hasSegments = false

	//	This created arrays of arrays, segmented into colours
	if (images && images.length > 0) {
		let index = 0
		images.forEach((img) => {
			output[index] = output[index] || []
			output[index].push(img)
			if (img.type === 'twentieth' || img.imageProcessingRequired === true) {
				index += 1
				hasSegments = true
			}
		})
	}

	if (!hasSegments) {
		return images
	}

	//	Extract a single image for each colour - just grab the full res version
	const result = output.map((img) => {
		const imgs = Array.isArray(img) ? img : [img]
		let imgOut = img[0]
		imgs.forEach((i) => {
			if (i.url && i.type === 'full') {
				imgOut = i
			}
		})

		return imgOut
	})

	return result
}

class ItemEdit extends React.Component {
	static getDerivedStateFromProps(props) {
		const parsedSearch = getNormalisedParameters(props.location.search)
		const fromKitEdit = getDecodedURIComponent(parsedSearch.fromkitedit) === 'true'
		const addNewItem = getDecodedURIComponent(parsedSearch.addnewitem)
		return { fromKitEdit, addNewItem }
	}

	constructor(props) {
		super(props)

		this.state = {}

		this.form = React.createRef()
		this.handleUploadImageError = this.handleUploadImageError.bind(this)
		this.handleValidFormSubmit = this.handleValidFormSubmit.bind(this)
		this.moveColourUpBinder = this.moveColourUpBinder.bind(this)
		this.moveColourDownBinder = this.moveColourDownBinder.bind(this)
		this.deleteColourBinder = this.deleteColourBinder.bind(this)
		this.handleUploadedImage = this.handleUploadedImage.bind(this)
	}

	componentDidMount() {
		this.props.appHeading({ text: this.context.translate('Add an item') })
		this.props.appShowBottomBar(false)
		this.props.appShowBottomNavBar(true)
		this.props.appBottomNavBarLabelNext(this.context.translate('DONE'))
		this.props.appBottomNavBarHandleClick(() => {
			this.form.current.submit()
		})
		if (!this.props.itemDraft) {
			this.props.itemDraftCreate()
		}
		this.props.appPageLoaded(true)
	}

	handleUploadedImage({ url }) {
		this.props.userProfileSetUploadedImage({ url })
	}

	handleUploadImageError() {
		this.props.appMessage(messageTypes.ERROR, this.context.translate('Item image upload failed'), errorTypes.UNKNOWNERROR)
	}

	moveColourUpBinder(colourIndex, store) { // eslint-disable-line class-methods-use-this
		return () => {
			const colours = arrayMove([...store.get('colours')], colourIndex, colourIndex - 1).map(col => ({ ...col, colourId: col.colourId || generateRandomID() }))
			store.set('colours', colours)
		}
	}

	moveColourDownBinder(colourIndex, store) { // eslint-disable-line class-methods-use-this
		return () => {
			const colours = arrayMove([...store.get('colours')], colourIndex, colourIndex + 1).map(col => ({ ...col, colourId: col.colourId || generateRandomID() }))
			store.set('colours', colours)
		}
	}

	deleteColourBinder(colourIndex, store) { // eslint-disable-line class-methods-use-this
		return () => {
			let colours = [...store.get('colours')]
			colours.splice(colourIndex, 1)
			colours = colours.map(col => ({ ...col, colourId: col.colourId || generateRandomID() }))
			store.set('colours', colours)
		}
	}

	handleInvalidFormSubmit() {
		this.props.appMessage(messageTypes.ERROR, this.context.translate('Item invalid, please check all the fields'), errorTypes.LOCALISEDERROR)
	}

	handleValidFormSubmit({ values }) { // eslint-disable-line class-methods-use-this
		let colours = []
		if (values.colours) {
			colours = values.colours.map((myColour, index) => {
				const col = { ...myColour }
				const imageUrls = {}
				if (col.images) {
					col.images.forEach((img) => {
						const imgUrl = img.originalUrl || img.url
						imageUrls[imgUrl] = { ...img }
					})
				}
				return {
					order: index,
					colourName: col.colourName,
					colourId: col.colourId,
					import: {
						images: Object.keys(imageUrls)
					}
				}
			})
		}

		this.props.appNextButtonIsLoading(true)
		const itemDraft = { item: { ...this.props.itemDraft, ...values }, colours }
		if (this.props.itemDraft.itemKey) {
			itemDraft.itemKey = this.props.itemDraft.itemKey
		}
		const doRedirect = (itemKey, itemColours) => {
			if (this.state.fromKitEdit) {
				//	Grab item from draft
				const aItem = {
					itemKey,
					...itemDraft.item,
					colour: itemColours[Object.keys(itemColours)[0]],
					category: [itemDraft.itemType]
				}
				//	Add to the kitDraft via saga, then redirect back to kitEdit
				this.props.kitDraftSelectItem({ aItem })
				this.props.appRedirect('/kit/edit')
			} else {
				this.props.appRedirect(`/item/${itemKey}`)
			}
		}

		this.props.itemVersionAdd({ itemDraft }, {
			//	The success function waits till the item's colours are all saved before redirecting, with a maximum wait for 10 seconds
			success: ({ itemKey }) => {
				const qs = firestore.collection('items').doc(itemKey).collection('colours')
				const inProgress = {}
				const itemColours = {}
				let unsubscribeObserver
				const timeoutRedirect = setTimeout(() => {
					unsubscribeObserver()
					this.props.appNextButtonIsLoading(false)
					doRedirect(itemKey, itemColours)
				}, 10000)
				unsubscribeObserver = qs.onSnapshot((querySnapshot) => {
					querySnapshot.forEach((qds) => {
						const data = qds.data()
						inProgress[qds.id] = (data.imageProcessingRequired === true || !data.images)
						itemColours[qds.id] = data
						const isInProgress = Object.keys(inProgress).filter(key => inProgress[key]).length > 0
						if (!isInProgress) {
							clearTimeout(timeoutRedirect)
							unsubscribeObserver()
							this.props.appNextButtonIsLoading(false)
							doRedirect(itemKey, itemColours)
						}
					})
				}, (err) => {
					console.error(`Encountered error: ${err}`)
				})
			},
			fail: () => {
				this.props.appNextButtonIsLoading(false)
			}
		})
	}

	render() {
		const itemDraft = this.state.addNewItem ? {} : this.props.itemDraft
		return (!itemDraft || !this.props.user.isAdmin) ? null : (
			<section>
				<div className={styles.surround}>
					<div className={styles.fieldSurround}>
						{!itemDraft.itemKey &&
							<p className={styles.note}>{this.context.translate('Note: Use this page to add an item only if you cannot find it otherwise, and be sure to click DONE when you\'re finished editing the item')}</p>
						}
						{itemDraft.itemKey &&
							<p className={styles.note}>{this.context.translate('Note: Be sure to click DONE when you\'re finished editing the item')}</p>
						}
					</div>
					<Form
						initialValues={{
							url: itemDraft.url,
							brandName: itemDraft.brandName,
							itemName: itemDraft.itemName,
							itemType: itemDraft.itemType || '',
							gender: itemDraft.gender || '',
							colours: itemDraft.colours || [],
							description: itemDraft.description
						}}
						onSubmit={args => (args.isValid ? this.handleValidFormSubmit(args) : this.handleInvalidFormSubmit(args))}
						ref={this.form}
					>
						{props => (
							<>
								<section className={styles.fieldSurroundNonPadded}>
									<Field
										component={TextField}
										name="url"
										value={props.values.url}
										fullWidth
										inputProps={{ 'data-lpignore': true }}
										margin="normal"
										label={this.context.translate('Link to item')}
										placeholder={this.context.translate('URL for the item you would like to add')}
										validate={{
											url: true,
											presence: true
										}}
									/>
								</section>
								<section className={styles.fieldSurround}>
									<Field
										component={AutoComplete}
										name="brandName"
										value={props.values.brandName}
										label={this.context.translate('Brand')}
										placeholder={this.context.translate('What brand is it?')}
										suggestions={this.props.brandSuggestions}
										suggestionKey={suggestion => suggestion.brandKey}
										suggestionValue={suggestion => suggestion.brandName}
										suggestionContent={suggestion => suggestion.brandName}
										onInputValueChange={query => this.props.brandSuggestionsFetch({ query })}
										onSuggestionSelected={(suggestion) => {
											props.store.setBinder('brandName')(undefined, suggestion.brandName)
											props.store.setBinder('brandKey')(undefined, suggestion.brandKey)
										}}
										validate={{
											presence: true
										}}
									/>
								</section>
								<section className={styles.fieldSurround}>
									<Field
										supportsHelperText={false}
										component={FormControl}
										name="itemType"
										validate={{
											presence: true
										}}
									>
										<InputLabel>{this.context.translate('Item type')}</InputLabel>
										<Select
											value={props.values.itemType}
											onChange={props.store.setBinder('itemType')}
										>
											{Object.keys(itemTypes).sort().map(it => <MenuItem key={it} value={it}>{itemTypes[it].name}</MenuItem>)}
										</Select>
									</Field>
								</section>
								<section className={styles.fieldSurround}>
									<Field
										supportsHelperText={false}
										component={FormControl}
										name="gender"
										validate={{
											presence: true
										}}
									>
										<InputLabel>{this.context.translate('Item gender')}</InputLabel>
										<Select
											value={props.values.gender}
											onChange={props.store.setBinder('gender')}
										>
											<MenuItem value="m">{this.context.translate('Male')}</MenuItem>
											<MenuItem value="f">{this.context.translate('Female')}</MenuItem>
											<MenuItem value="u">{this.context.translate('Unisex')}</MenuItem>
										</Select>
									</Field>
								</section>
								<section className={styles.fieldSurroundNonPadded}>
									<Field
										component={TextField}
										name="itemName"
										value={props.values.itemName}
										fullWidth
										margin="normal"
										label={this.context.translate('Name')}
										validate={{
											presence: true
										}}
									/>
								</section>
								<section className={styles.fieldSurroundNonPadded}>
									<Field
										component={TextField}
										name="description"
										value={props.values.caption}
										fullWidth
										multiline
										margin="normal"
										label={this.context.translate('Description')}
									/>
								</section>
								<h2>{this.context.translate('Colours')}</h2>
								<section className={styles.fieldSurroundNonPadded}>
									{props.values.colours.map((colour, index) => (
										<div className={styles.imageSurround} key={`colour-${index}-${colour.colourId || index}`}>{/* eslint-disable-line react/no-array-index-key */}
											<Field
												component={ColourEditor}
												name="colours"
												arrayIndex={index}
												colourIndex={index}
												getComponentValue={values => ({ ...colour, ...values })}
												saveUploadedImage={this.handleUploadedImage}
												errorHandler={this.handleUploadImageError}
												colourName={colour.colourName}
												colourId={colour.colourId}
												images={segmentImages(colour.images)}
												validate={{
													presence: true,
													asyncValidator: {
														validate: (value) => {
															if (!value) {
																return undefined
															}
															if (!(value.colourName && value.colourName.length)) {
																return 'colourName missing'
															}
															const canonicalColours = props.values.colours.filter(otherColour => Item.isSameColour(value, otherColour))
															if (canonicalColours.length > 1) {
																return `duplicate colourName (${canonicalColours.map(c => c.colourName)})`
															}
															return undefined
														}
													}
												}}
											/>
											<div className={styles.colourButtonSurround}>
												<IconButton disabled={index === 0} aria-label="Context" onClick={this.moveColourUpBinder(index, props.store)}>
													<ArrowUpward />
												</IconButton>
												<IconButton disabled={index === (props.values.colours.length - 1)} aria-label="Context" onClick={this.moveColourDownBinder(index, props.store)}>
													<ArrowDownward />
												</IconButton>
												<IconButton aria-label="Context" onClick={this.deleteColourBinder(index, props.store)}>
													<Delete />
												</IconButton>
											</div>
										</div>
									))}
								</section>
								<section className={styles.fieldSurroundNonPadded}>
									<div className={styles.buttonSurround}>
										<Button
											variant="contained"
											onClick={() => {
												const colours = props.store.get('colours') || []
												colours.push({ colourName: '' })
												props.store.set('colours', colours)
											}}
										>{this.context.translate('Add a colour')}
										</Button>
									</div>
								</section>
							</>
						)}
					</Form>
				</div>
			</section>
		)
	}
}

ItemEdit.propTypes = {
	itemDraft: PropTypes.object, // eslint-disable-line react/forbid-prop-types
	itemDraftCreate: PropTypes.func.isRequired,
	itemVersionAdd: PropTypes.func.isRequired,
	userProfileSetUploadedImage: PropTypes.func.isRequired,
	appBottomNavBarLabelNext: PropTypes.func.isRequired,
	appBottomNavBarHandleClick: PropTypes.func.isRequired,
	appNextButtonIsLoading: PropTypes.func.isRequired,
	appPageLoaded: PropTypes.func.isRequired,
	appRedirect: PropTypes.func.isRequired,
	appShowBottomBar: PropTypes.func.isRequired,
	appHeading: PropTypes.func.isRequired,
	appShowBottomNavBar: PropTypes.func.isRequired,
	appMessage: PropTypes.func.isRequired,
	kitDraftSelectItem: PropTypes.func.isRequired,
	brandSuggestions: PropTypes.array, // eslint-disable-line react/forbid-prop-types
	brandSuggestionsFetch: PropTypes.func.isRequired,
	location: PropTypes.shape({
		pathname: PropTypes.string.isRequired,
		search: PropTypes.string
	}).isRequired,
	user: PropTypes.object.isRequired // eslint-disable-line react/forbid-prop-types
}

ItemEdit.defaultProps = {
	itemDraft: undefined,
	brandSuggestions: undefined
}

ItemEdit.contextTypes = {
	translate: PropTypes.func
}

export const testing = {
	ItemEdit
}

export default connectPropTypes(ItemEdit)



// WEBPACK FOOTER //
// ./src/ItemEdit.jsx